import { Component, DestroyRef, inject, OnDestroy, OnInit, PLATFORM_ID, REQUEST } from '@angular/core';
import { CategoryStore } from '../store/category.store';
import { PromosStore } from '../store/promos.store';
import { Meta, MetaDefinition } from '@angular/platform-browser';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { ConfigurationService } from '../services/configuration.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CategoryService } from '../services/category.service';
import { switchMap, tap } from 'rxjs';
import { HeaderComponent } from './header/header.component';
import { CategoryComponent } from './category/category.component';
import { MenusComponent } from './menus/menus.component';
import { PromosComponent } from './promos/promos.component';
import { MapsComponent } from '../shared/maps/maps.component';
import { AdvertisementFlowService } from '../services/advertisement-flow.service';

@Component({
  selector: 'app-welcome',
  templateUrl: './welcome.component.html',
  styleUrl: './welcome.component.scss',
  imports: [HeaderComponent, CategoryComponent, MenusComponent, PromosComponent, MapsComponent],
  standalone: true,
})
export class WelcomeComponent implements OnInit, OnDestroy {
  private categoryStore = inject(CategoryStore);
  private promosStore = inject(PromosStore);

  private platformId = inject(PLATFORM_ID);
  private request = inject(REQUEST);
  private destroyRef = inject(DestroyRef);

  private configService = inject(ConfigurationService);
  private categoryService = inject(CategoryService);
  private metaService = inject(Meta);

  private adFlowService = inject(AdvertisementFlowService);

  private metaTags: HTMLMetaElement[] = [];
  private config: any | null = null;
  private categories: any[] = [];

  constructor() {}

  ngOnInit() {
    this.categoryStore.loadCategories();
    this.promosStore.loadPromotions(['product-offer', 'order-offer']);
    this.promosStore.loadPromotions(['product-discount', 'order-discount']);

    this.configService.getPublicConfiguration()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(res => {
          this.config = res.body?.data;
        }),
        switchMap(() => this.categoryService.getAllCategory())
      )
      .subscribe((res) => {
        this.categories = res.body?.data || [];
        this.setSeoTags();
      });

    this.loadAdvertisements();
  }

  private getDeliveryCityId(): string | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    try {
      const raw = localStorage.getItem('order_context_type');
      if (!raw) return null;

      const context = JSON.parse(raw);
      if (context?.orderType === 'En Livraison' && context?.cityId) {
        return context.cityId;
      }
      return null;
    } catch {
      return null;
    }
  }

  private loadAdvertisements(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const deliveryCityId = this.getDeliveryCityId();

    // Appel unique au service centralisé
    this.adFlowService.checkAndShowAdvertisements(deliveryCityId, !!deliveryCityId);
  }

  private setSeoTags() {
    const description = this.generateHomepageDescription();
    const keywords = this.generateHomepageKeywords();

    this.addTag({ name: 'description', content: description });
    this.addTag({ name: 'keywords', content: keywords });
    this.addTag({ name: 'viewport', content: 'width=device-width, initial-scale=1' });

    this.addTag({ property: 'og:title', content: `${this.config?.name} - Commandez en ligne` });
    this.addTag({ property: 'og:description', content: description.substring(0, 300) });
    this.addTag({ property: 'og:image', content: this.config?.logo || this.config?.extras?.['logo'] });
    this.addTag({ property: 'og:image:alt', content: this.config?.name });
    this.addTag({ property: 'og:type', content: 'website' });

    if (isPlatformBrowser(this.platformId)) {
      this.addTag({ property: 'og:url', content: window.location.href });
    }
    if (isPlatformServer(this.platformId) && this.request?.url) {
      this.addTag({ property: 'og:url', content: this.request.url });
    }
    this.addTag({ property: 'og:site_name', content: this.config?.name });

    this.addTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.addTag({ name: 'twitter:title', content: `${this.config?.name} - Livraison rapide` });
    this.addTag({ name: 'twitter:description', content: description.substring(0, 200) });
    this.addTag({ name: 'twitter:image', content: this.config?.logo || this.config?.extras?.['logo'] });

    if (this.config?.address) {
      this.addTag({ name: 'geo.placename', content: this.config?.address });
    }

    if (this.config?.phones && this.config?.phones.length) {
      this.addTag({ name: 'tel', content: this.config?.phones[0] });
    }
  }

  private generateHomepageDescription(): string {
    const parts = [];

    parts.push(`${this.config?.name} vous propose une large sélection de plats délicieux`);

    if (this.categories) {
      const mainCategories = this.categories
        .filter((c: any) => c.products > 0)
        .sort((a: any, b: any) => (b.products || 0) - (a.products || 0))
        .slice(0, 6);

      if (mainCategories.length) {
        const categoryNames = mainCategories.map((c: any) => c.name).join(', ');
        parts.push(`Découvrez nos ${categoryNames}`);
      }
    }

    const address = this.config?.address?.split(',')[1];
    if (address) {
      parts.push(`Livraison rapide à ${address} et ses alentours`);
    }

    if (this.config?.phones && this.config?.phones.length) {
      parts.push(`Commandez au ${this.config?.phones[0]}`);
    }

    let description = parts.join('. ');
    if (description.length > 160) {
      description = description.substring(0, 157) + '...';
    }

    return description;
  }

  private generateHomepageKeywords(): string {
    const keywords = new Set<string>();

    keywords.add('livraison repas');
    keywords.add('commande en ligne');
    keywords.add('restaurant');
    keywords.add('pizzeria');
    keywords.add('fast food');

    if (this.categories) {
      this.categories.forEach((category: any) => {
        if (category.name && category.products > 0) {
          keywords.add(category.name.toLowerCase());
        }
      });
    }

    if (this.config?.address) {
      const addressParts = this.config?.address.split(',');
      if (addressParts.length >= 2) {
        const city = addressParts[1].trim();
        keywords.add(`livraison ${city}`);
        keywords.add(`restaurant ${city}`);
        keywords.add(`${city} livraison`);
      }
    }

    keywords.add('à emporter');
    keywords.add('livraison à domicile');
    keywords.add('paiement en ligne');

    keywords.add('promotion');
    keywords.add('offre spéciale');

    const keywordArray = Array.from(keywords);
    return keywordArray.slice(0, 25).join(', ');
  }

  private addTag(tag: MetaDefinition, forceCreation?: boolean) {
    const t = this.metaService.addTag(tag, forceCreation);
    if (t) this.metaTags.push(t);
  }

  private removeSeoTags() {
    this.metaTags.forEach(tag => this.metaService.removeTagElement(tag));
    console.log(`Cleared ${this.metaTags.length} tags`);
    this.metaTags = [];
  }

  ngOnDestroy(): void {
    this.removeSeoTags();
  }
}
